import { Question } from "../../common/types/QuestionnaireEntity";
import {
  QuestionnaireResponse,
  ResponseItem,
} from "../../common/types/QuestionnaireResponse";
import {
  AnswerObject,
  QuestionnaireAnswer,
  QuestionnaireState,
} from "../questionnaire/questionnaireSlice";

const makeAnswerObject = (
  question: Question,
  userResponse?: ResponseItem
): QuestionnaireAnswer => {
  const answerObject: QuestionnaireAnswer = {
    questionId: question?.id as string,
    questionType: question?.type as string,
    patientId: "",
    value: {
      response: undefined,
      multiSelect: [],
      detailedResponse: "",
    },
  };
  if (question.type === "open-choice") {
    answerObject.value.multiSelect =
      userResponse?.answer?.map((elem) => elem.value.string) ?? [];
  } else {
    answerObject.value.response = userResponse?.answer?.at(0)?.value.string;
  }
  if (userResponse?.item) {
    answerObject.value.detailedResponse = userResponse?.item
      ?.at(0)
      ?.answer?.at(0)?.value?.string;
  }
  return answerObject;
};
// Selector utilities
const convertStringResponseToNumeric = (response: string): number => {
  const aValues = ["Not at all"];
  const bValues = ["Several days"];
  const cValues = ["More than half the days"];
  const dValues = ["Nearly every day"];
  if (aValues.includes(response)) {
    return 0;
  } else if (bValues.includes(response)) {
    return 1;
  } else if (cValues.includes(response)) {
    return 2;
  } else if (dValues.includes(response)) {
    return 3;
  }
  return 0;
};

const calculateEmotionalAssessmentScore = (
  questionnaireResponse: QuestionnaireResponse
) => {
  const scoreEmotions = { phq9: 0, gad7: 0 };
  for (const item of questionnaireResponse.item) {
    const resp = item.answer?.at(0) ? item.answer[0].value.string : "";
    if (parseFloat(item.id) <= 9) {
      scoreEmotions.phq9 += convertStringResponseToNumeric(resp);
    } else if (parseFloat(item.id) >= 10) {
      scoreEmotions.gad7 += convertStringResponseToNumeric(resp);
    }
  }
  return scoreEmotions;
};

const getIsEnabled = (question: Question, answers: AnswerObject) => {
  if (question.enableWhen) {
    for (const enable of question.enableWhen) {
      const answer = answers[enable.question];
      if (enable.operator === "=") {
        if (answer) {
          const responseMatch =
            answer.value.response?.toString() === enable.answer.string;
          const multiMatch = answer.value.multiSelect?.includes(
            enable.answer.string
          );
          if (responseMatch || multiMatch) {
            return true;
          }
        }
      } else if (enable.operator === "!=") {
        if (answer) {
          const responseMatch =
            answer.value.response?.toString() !== enable.answer.string;
          const multiMatch = !answer.value.multiSelect?.includes(
            enable.answer.string
          );
          if (responseMatch && multiMatch) {
            return true;
          }
        }
      }
    }
    return false;
  }
  return true;
};

const calculateQuestionnaireScore = (
  state: QuestionnaireState,
  questionnaireId: string
) => {
  const questionnaireResponse = state.responses.find(
    (elem) => elem.questionnaire === questionnaireId
  );
  if (questionnaireResponse) {
    switch (questionnaireId) {
      // if engagement or lifestyle, no score reported
      case "patient-engagement-preferences-questionnaire":
      case "patient-lifestyle-questionnaire":
        return undefined;
      // if emotional, has two scores, one for phq9, one for and gad7
      case "patient-emotional-wellness-questionnaire":
        return calculateEmotionalAssessmentScore(questionnaireResponse);
      // home and environment score has a numerical score and need flag - not implementing now
      // health screening has multiple flags, PGx flag, vitamin d flag, prevent flag
      case "patient-health-screening-questionnaire":
        return calculateGeneralAssessmentScore(questionnaireResponse);
      default:
        return undefined;
    }
  } else {
    return undefined;
  }
};

const getResponseValue = (
  response: QuestionnaireResponse,
  index: string
): string | undefined => {
  const answer = response.item.find((elem) => elem.id === index)?.answer;
  if (answer) {
    return answer.map((elem) => elem.value.string).join(", ");
  }
  return undefined;
};

const calculateGeneralAssessmentScore = (
  questionnaireResponse: QuestionnaireResponse
) => {
  let pgx = false;
  let vitamind = false;
  let prevent = false;
  if (
    getResponseValue(questionnaireResponse, "2")?.includes("Ashkenazi Jewish")
  ) {
    prevent = true;
  }
  if (
    getResponseValue(questionnaireResponse, "5") === "Yes" ||
    getResponseValue(questionnaireResponse, "6") === "Yes"
  ) {
    pgx = true;
  }
  if (getResponseValue(questionnaireResponse, "8") === "Yes") {
    vitamind = true;
  }
  if (
    getResponseValue(questionnaireResponse, "16") === "Yes" ||
    getResponseValue(questionnaireResponse, "18") === "Yes" ||
    getResponseValue(questionnaireResponse, "19") === "Yes" ||
    getResponseValue(questionnaireResponse, "20") === "Yes" ||
    getResponseValue(questionnaireResponse, "23") === "Yes" ||
    getResponseValue(questionnaireResponse, "24") === "Yes" ||
    getResponseValue(questionnaireResponse, "25") === "Yes"
  ) {
    prevent = true;
  }
  return { pgx, vitamind, prevent };
};

const calculateCompletion = (
  state: QuestionnaireState,
  questionnaireID: string
) => {
  const questionnaire = state.questionnaires?.find(
    (elem) => elem.id === questionnaireID
  );
  const questionCount = questionnaire?.item?.length ?? 0;

  if (!questionnaire || !questionCount) {
    return 0;
  }

  const responseIndex: number = state.responses?.findIndex(
    (elem: QuestionnaireResponse) => {
      return elem.questionnaire === questionnaireID;
    }
  );

  if (responseIndex >= 0) {
    const answers = state.responses[responseIndex].item;

    const totals = questionnaire.item.reduce(
      (out: { answered: number; questions: number }, q: any) => {
        if (!q.enableWhen) {
          out.questions = out.questions + 1;
          const hasAnswer = answers.find((answer: any) => q.id === answer.id);
          out.answered = out.answered + (hasAnswer ? 1 : 0);
        }
        return out;
      },
      { answered: 0, questions: 0 }
    );

    return Math.floor((totals.answered / totals.questions) * 100);
  }
  return 0;
};
// End Selector utilities

export {
  calculateCompletion,
  calculateEmotionalAssessmentScore,
  calculateGeneralAssessmentScore,
  calculateQuestionnaireScore,
  convertStringResponseToNumeric,
  getIsEnabled,
  makeAnswerObject,
};
